export const parseXML = (xmlString) => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(xmlString, 'text/xml')

  const parserError = doc.querySelector('parsererror')
  if (parserError) {
    throw new Error('Failed to parse XML: ' + parserError.textContent)
  }

  return doc
}

const getElementText = (element, selector) => {
  if (!element) return ''
  const el = element.querySelector(selector)
  return el ? el.textContent.trim() : ''
}

const getElementAttribute = (element, selector, attribute) => {
  if (!element) return ''
  const el = element.querySelector(selector)
  return el ? el.getAttribute(attribute) || '' : ''
}

export const extractImageFromItem = (item, format) => {
  if (!item) return ''

  const enclosure = item.querySelector('enclosure[type^="image"]')
  if (enclosure) {
    return enclosure.getAttribute('url') || ''
  }

  const mediaContent = item.querySelector('content[medium="image"], content[type^="image"]')
  if (mediaContent) {
    return mediaContent.getAttribute('url') || ''
  }

  const mediaThumbnail = item.querySelector('thumbnail')
  if (mediaThumbnail) {
    return mediaThumbnail.getAttribute('url') || ''
  }

  const content = format === 'atom'
    ? getElementText(item, 'content')
    : getElementText(item, 'description') || getElementText(item, 'content\\:encoded')

  if (content) {
    const imgMatch = content.match(/<img[^>]+src="([^">]+)"/)
    if (imgMatch) {
      return imgMatch[1]
    }
  }

  return ''
}

const parseRSSItem = (item, index) => {
  const title = getElementText(item, 'title')
  const description = getElementText(item, 'description')
  const contentEncoded = getElementText(item, 'content\\:encoded')
  const link = getElementText(item, 'link')
  const guid = getElementText(item, 'guid') || link || `item-${index}`
  const pubDate = getElementText(item, 'pubDate')
  const author = getElementText(item, 'author') || getElementText(item, 'dc\\:creator')

  const categoryElements = item.querySelectorAll('category')
  const categories = Array.from(categoryElements).map(cat => cat.textContent.trim())

  return {
    id: guid,
    title,
    description,
    content: contentEncoded || description,
    link,
    pubDate: pubDate ? new Date(pubDate) : null,
    author,
    categories,
    image: extractImageFromItem(item, 'rss'),
    guid,
    raw: item
  }
}

const parseAtomEntry = (entry, index) => {
  const title = getElementText(entry, 'title')
  const summary = getElementText(entry, 'summary')
  const content = getElementText(entry, 'content')
  const linkEl = entry.querySelector('link[rel="alternate"], link:not([rel])')
  const link = linkEl ? linkEl.getAttribute('href') || '' : ''
  const id = getElementText(entry, 'id') || link || `entry-${index}`
  const published = getElementText(entry, 'published') || getElementText(entry, 'updated')
  const authorName = getElementText(entry, 'author name')

  const categoryElements = entry.querySelectorAll('category')
  const categories = Array.from(categoryElements).map(cat =>
    cat.getAttribute('term') || cat.getAttribute('label') || cat.textContent.trim()
  ).filter(Boolean)

  return {
    id,
    title,
    description: summary || content,
    content: content || summary,
    link,
    pubDate: published ? new Date(published) : null,
    author: authorName,
    categories,
    image: extractImageFromItem(entry, 'atom'),
    guid: id,
    raw: entry
  }
}

export const detectFeedFormat = (doc) => {
  if (doc.querySelector('rss')) return 'rss'
  if (doc.querySelector('feed')) return 'atom'
  throw new Error('Unknown feed format. Expected RSS 2.0 or Atom.')
}

const parseRSS = (doc) => {
  const channel = doc.querySelector('channel')
  if (!channel) {
    throw new Error('Invalid RSS feed: missing channel element')
  }

  const feed = {
    title: getElementText(channel, 'title'),
    description: getElementText(channel, 'description'),
    link: getElementText(channel, 'link'),
    image: getElementAttribute(channel, 'image url', 'textContent') ||
           getElementText(channel, 'image url')
  }

  const itemElements = channel.querySelectorAll('item')
  const items = Array.from(itemElements).map((item, index) => parseRSSItem(item, index))

  return { feed, items }
}

const parseAtom = (doc) => {
  const feedEl = doc.querySelector('feed')
  if (!feedEl) {
    throw new Error('Invalid Atom feed: missing feed element')
  }

  const linkEl = feedEl.querySelector('link[rel="alternate"], link:not([rel])')
  const iconEl = feedEl.querySelector('icon, logo')

  const feed = {
    title: getElementText(feedEl, 'title'),
    description: getElementText(feedEl, 'subtitle'),
    link: linkEl ? linkEl.getAttribute('href') || '' : '',
    image: iconEl ? iconEl.textContent.trim() : ''
  }

  const entryElements = feedEl.querySelectorAll('entry')
  const items = Array.from(entryElements).map((entry, index) => parseAtomEntry(entry, index))

  return { feed, items }
}

export const parseRSSFeed = (xmlString) => {
  const doc = parseXML(xmlString)
  const format = detectFeedFormat(doc)

  if (format === 'rss') {
    return parseRSS(doc)
  } else if (format === 'atom') {
    return parseAtom(doc)
  }

  throw new Error('Unsupported feed format')
}

export const filterRSSItems = (items, filters = {}) => {
  if (!filters || Object.keys(filters).length === 0) {
    return items
  }

  return items.filter(item => {
    if (filters.searchTerm) {
      const search = filters.searchTerm.toLowerCase()
      const inTitle = item.title?.toLowerCase().includes(search)
      const inDescription = item.description?.toLowerCase().includes(search)
      if (!inTitle && !inDescription) return false
    }

    if (filters.dateFrom && item.pubDate) {
      if (item.pubDate < filters.dateFrom) return false
    }
    if (filters.dateTo && item.pubDate) {
      if (item.pubDate > filters.dateTo) return false
    }

    if (filters.categories && filters.categories.length > 0) {
      if (!item.categories || item.categories.length === 0) return false
      const hasCategory = filters.categories.some(cat =>
        item.categories.includes(cat)
      )
      if (!hasCategory) return false
    }

    if (filters.author) {
      if (!item.author?.toLowerCase().includes(filters.author.toLowerCase())) {
        return false
      }
    }

    return true
  })
}

export const paginateItems = (items, take, skip = 0) => {
  if (!items) return []
  if (!take && !skip) return items

  const start = skip || 0
  const end = take ? start + take : items.length

  return items.slice(start, end)
}

export const formatRSSDate = (date, format = 'relative') => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return ''
  }

  switch (format) {
    case 'relative': {
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) !== 1 ? 's' : ''} ago`
      return date.toLocaleDateString()
    }
    case 'full':
      return date.toLocaleString()
    case 'short':
      return date.toLocaleDateString()
    default:
      return date.toLocaleDateString()
  }
}

export const truncateText = (text, maxLength) => {
  if (!text || !maxLength || text.length <= maxLength) {
    return text || ''
  }

  return text.substring(0, maxLength).trim() + '...'
}
